import type { TList } from '@/services/api';
import type { TNetworkError } from '@/services/errors';
import type { TMemberDTO, TMemberVisibilityDTO } from './members.dto';

import { BaseService, toAscending, toRange, toSearchFilter } from '@/services/api';

import type {
  TMember,
  TMemberFilterQuery,
  TMemberListQuery,
  TMemberVisibility,
} from './members.types';

import {
  BILLING_MEMBER_FIELDS,
  LIST_MEMBER_FIELDS,
  MEMBER_VISIBILITY_FIELDS,
  SEARCH_MEMBER_COLUMNS,
} from './members.fields';
import { toMember, toMemberVisibility } from './members.adapters';

class MembersService extends BaseService {
  protected readonly serviceName = 'membersService';

  private toFilteredMembers(query: TMemberFilterQuery, isCountOnly: boolean) {
    const {
      role, search, statuses, paymentStatus, activatedSince, isArchived, isComped, isInMarket, conciergeID, unseenBy, memberIDs,
    } = query;

    const hasBillingFilter = paymentStatus !== undefined || isComped !== undefined;
    const fields = hasBillingFilter ? BILLING_MEMBER_FIELDS : LIST_MEMBER_FIELDS;

    let builder = this.db
      .from('roster')
      .select(fields, { count: 'exact', head: isCountOnly })
      .eq('role', role);

    if (paymentStatus) {
      builder = builder.eq('clients.payment_status', paymentStatus);
    }

    if (isComped !== undefined) {
      builder = builder.eq('clients.is_comped', isComped);
    }

    if (isInMarket) {
      builder = builder.not('market_id', 'is', null);
    }

    if (statuses && statuses.length > 0) {
      builder = builder.in('status', statuses);
    }

    if (activatedSince) {
      builder = builder.gte('activated_at', activatedSince);
    }

    if (isArchived) {
      builder = builder.not('archived_at', 'is', null);
    }

    if (conciergeID) {
      builder = builder.eq('assigned_concierge_id', conciergeID);
    }

    if (unseenBy) {
      builder = builder.or(`concierge_seen_by.is.null,concierge_seen_by.neq.${unseenBy}`);
    }

    if (memberIDs) {
      builder = builder.in('user_id', memberIDs);
    }

    if (search) {
      builder = builder.or(toSearchFilter(SEARCH_MEMBER_COLUMNS, search));
    }

    return builder;
  }

  public listMembers(query: TMemberListQuery): Promise<TList<TMember> | TNetworkError> {
    const { sortBy = 'activated_at', sortDirection } = query;
    const { from, to } = toRange(query);

    const builder = this.toFilteredMembers(query, false)
      .order(sortBy, { ascending: toAscending(sortDirection), nullsFirst: false })
      .order('user_id', { ascending: true })
      .range(from, to);

    return this.runList(builder.returns<TMemberDTO[]>(), 'listMembers', toMember);
  }

  public countMembers(query: TMemberFilterQuery): Promise<number | TNetworkError> {
    return this.runCount(this.toFilteredMembers(query, true), 'countMembers');
  }

  public readVisibility(memberID: string): Promise<TMemberVisibility | TNetworkError> {
    return this.run(
      this.db
        .from('users')
        .select(MEMBER_VISIBILITY_FIELDS)
        .eq('id', memberID)
        .single()
        .returns<TMemberVisibilityDTO>(),
      'readVisibility',
      toMemberVisibility,
    );
  }
}

const membersService = new MembersService();

export {
  membersService,
};
